import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { isFunction, isPlainObject } from "es-toolkit";
import { debounceTime } from "rxjs/operators";
import { sntLog } from "src/app/common/logger";
import {
  BrokerEventListener,
  BrokerEventListenerWithResources
} from "src/app/sse/broker-event.types";
import { BrokerService } from "src/app/sse/broker.service";
import { SseEvents } from "src/app/sse/sse-events";
import { VisibilityService } from "src/app/services/visibility.service";
import { CURRENT_TAB_FACTORY } from "src/app/tokens/current-tab.token";

export type SSERegister<T = any> = (
  eventTypes: SseEvents[],
  eventHandler: BrokerEventListener
) => void;

export type SSESetupReturnType = Array<
  [SseEvents[], BrokerEventListener | BrokerEventListenerWithResources]
>;

export type PageVisibleHandler = () => void | Promise<void>;

const PAGE_VISIBLE_DEBOUNCE_MS = 1000;

const logger = sntLog("SseComponent");

@Component({ template: "", standalone: true })
export abstract class SseComponent implements OnInit, OnDestroy {
  @Input()
  disableSse: boolean = false;

  private readonly brokerService = inject(BrokerService);
  private readonly visibilityService = inject(VisibilityService);
  readonly #visibilityDestroyRef = inject(DestroyRef);

  readonly #idsOfRegisteredListeners: Array<{ id: string; type: SseEvents }> =
    [];

  readonly #pageVisibleHandlers: PageVisibleHandler[] = [];

  readonly CURRENT_TAB = inject(CURRENT_TAB_FACTORY)();

  constructor() {
    this.visibilityService.pageVisible$
      .pipe(
        debounceTime(PAGE_VISIBLE_DEBOUNCE_MS),
        takeUntilDestroyed(this.#visibilityDestroyRef)
      )
      .subscribe(() => {
        void this.#invokePageVisibleHandlers();
      });
  }

  protected registerOnPageVisible(handler: PageVisibleHandler): void {
    this.#pageVisibleHandlers.push(handler);
  }

  ngOnInit(): void {
    if (!this.disableSse) {
      const setupConfig: SSESetupReturnType = this.setupSSEHandlers();

      if (setupConfig) {
        setupConfig.forEach(
          ([eventTypes, ...callbackOrCallBackWithResourcesFactory]) => {
            const callBacksWithoutResourcesFactories: BrokerEventListener[] = <
              BrokerEventListener[]
            >callbackOrCallBackWithResourcesFactory.filter((x) =>
              isFunction(x)
            );

            const callBacksWithResourcesFactories: BrokerEventListenerWithResources[] =
              <BrokerEventListenerWithResources[]>(
                callbackOrCallBackWithResourcesFactory.filter((x) =>
                  isPlainObject(x)
                )
              );

            eventTypes.forEach((eventType) => {
              [
                ...callBacksWithoutResourcesFactories.map((listener) => ({
                  listener,
                  resourcesFactory: () => Promise.resolve([] as string[])
                })),
                ...callBacksWithResourcesFactories
              ].forEach(({ listener, resourcesFactory }) => {
                const id: string = this.brokerService.addListener({
                  type: eventType,
                  listener,
                  resourcesFactory,
                  tab: this.CURRENT_TAB
                });

                this.#idsOfRegisteredListeners.push({
                  id,
                  type: eventType
                });
              });
            });
          }
        );
      }
    }
  }

  ngOnDestroy() {
    this.#idsOfRegisteredListeners.forEach(({ id, type }) => {
      this.brokerService.removeListener({ id, type });
    });
  }

  abstract setupSSEHandlers(): SSESetupReturnType;

  async #invokePageVisibleHandlers(): Promise<void> {
    const results = await Promise.allSettled(
      this.#pageVisibleHandlers.map((handler) => Promise.resolve(handler()))
    );

    results.forEach((result) => {
      if (result.status === "rejected") {
        logger.error(result.reason);
      }
    });
  }
}
